import {
  ReactNode,
  useEffect,
  useState,
  forwardRef,
  useRef,
  RefObject,
} from "react";
import stylesLabelled from "./TextFieldAtomLabelled.module.css";
import stylesLabelless from "./TextFieldAtomLabelless.module.css";

import { motion, AnimatePresence } from "framer-motion";

import {
  LabelPairedCircleCaptionBoldIcon,
  LabelPairedCircleCheckCaptionBoldIcon,
  LabelPairedCircleCheckSmBoldIcon,
  LabelPairedCircleXmarkCaptionBoldIcon,
  LabelPairedTriangleExclamationSmBoldIcon,
} from "@deriv/quill-icons";
import { hasInputValue } from "../../helpers";

const textFieldStyles = {
  labelled: stylesLabelled,
  labelless: stylesLabelless,
};

export type TextFieldMessagesType = {
  text: string;
  type: "neutral" | "fail" | "success";
  icon?: ReactNode;
};

export type TextFieldAtomType = {
  id?: string;
  name?: string;
  className?: string;
  label?: string;
  showCharacterCounter?: boolean;
  message?: string;
  placeholder?: string;

  /** Variant props */
  indicator?: "neutral" | "fail" | "success";
  state?: "default" | "disabled";
  type?: "outline" | "fill";
  textAlignment?: "left" | "center";

  /** Custom props */
  isGroup?: boolean;
  isLeftGroup?: boolean;
  variant?: "labelled" | "labelless";
  iconLeft?: ReactNode;
  iconLeftSecondary?: ReactNode;
  iconRight?: ReactNode;
  iconStatus?: ReactNode;
  iconExtra?: ReactNode;
  labelLeft?: string;
  labelLeftValue?: string;
  labelRight?: string;
  contentRight?: ReactNode;
  isRequired?: boolean;
  optionalText?: string;
  maxLength?: number;
  inputType?: "text" | "password" | "number" | "email" | "tel" | "url";
  inputElement?: "input" | "textarea";
  size?: "lg" | "md" | "sm";
  showStatusIcon?: boolean;
  value?: string | number;
  allowTyping?: boolean;
  messages?: TextFieldMessagesType[];
  containerRef?: React.RefObject<HTMLDivElement>;
  customContent?: ReactNode;
  customInnerContent?: ReactNode;
  onChange?: (value: string | number) => void;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onAddonLeftClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onBeforeChange?: (value: string | number) => boolean;
};

const TextFieldAtom = forwardRef<HTMLInputElement, TextFieldAtomType>(
  (
    {
      id,
      name,
      className = "",
      indicator = "neutral",
      state = "default",
      type = "outline",
      inputType = "text",
      inputElement = "input",
      showCharacterCounter = false,
      isGroup = false,
      isLeftGroup = false,
      label = "",
      iconLeft,
      iconLeftSecondary,
      iconRight,
      iconStatus,
      iconExtra,
      labelLeft = "",
      labelLeftValue = "",
      labelRight = "",
      isRequired = false,
      optionalText = "",
      maxLength = 32,
      message = "",
      messages = [],
      placeholder = "Placeholder",
      variant = "labelled",
      textAlignment = "left",
      size = "md",
      showStatusIcon = true,
      value: inputValue,
      allowTyping = true,
      containerRef,
      customContent,
      customInnerContent,
      contentRight,
      onChange,
      onClick,
      onAddonLeftClick,
      onFocus,
      onBlur,
      onBeforeChange,
    },
    ref
  ) => {
    const styles = textFieldStyles[variant];

    const MESSAGE_STATUS_STYLES = {
      neutral: styles.checklist1,
      success: styles.checklist2,
      fail: styles.checklist3,
    } as const;

    const STATUS_ICONS = {
      success: <LabelPairedCircleCheckSmBoldIcon />,
      fail: <LabelPairedTriangleExclamationSmBoldIcon />,
      neutral: null,
    } as const;

    const MESSAGE_ICONS = {
      success: <LabelPairedCircleCheckCaptionBoldIcon />,
      fail: <LabelPairedCircleXmarkCaptionBoldIcon />,
      neutral: <LabelPairedCircleCaptionBoldIcon />,
    } as const;

    const textFieldId = `quill-atom-textfield-${variant}-${indicator}-${state}-${type}-${size}-${textAlignment}-${
      isGroup ? "group" : ""
    }`;

    const [isFocused, setIsFocused] = useState(hasInputValue(inputValue));
    const [value, setValue] = useState<string | number>(inputValue || "");

    const statusIcon = iconStatus ? iconStatus : STATUS_ICONS[indicator];

    // Create internal ref if external ref is not provided
    const internalRef =
      inputElement === "input"
        ? useRef<HTMLInputElement>(null)
        : useRef<HTMLTextAreaElement>(null);
    const inputRef = ref || internalRef;

    useEffect(() => {
      if (isFocused) {
        if (typeof inputRef === "object" && inputRef?.current) {
          inputRef.current.focus();
        }
      }
    }, [isFocused, inputRef]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (state === "disabled") return;

      setIsFocused(true);
      onFocus?.();
    };

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (!allowTyping) return;

      const newValue = e.target.value;
      if (newValue.length <= maxLength) {
        setValue(newValue);
        onChange?.(newValue);
      }
    };
    const handleBlur = () => {
      if (!hasInputValue(value)) {
        setIsFocused(false);
        onBlur?.();
      }
    };

    const handleKeyInput = (
      e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      // @ts-expect-error: isAction is injected by Storybook for action handlers
      if (typeof onBeforeChange === "function" && !onBeforeChange?.isAction) {
        if (!onBeforeChange(e.key)) {
          e.preventDefault();
        }
      }
    };

    const inputProps = {
      id: id || textFieldId,
      name: name,
      className: `${styles.input} quill-ui-next-input ${
        !allowTyping && "quill-ui-next-no-typing"
      }`,
      value,
      onChange: handleChange,
      placeholder,
      type: inputType,
      onFocus: () => setIsFocused(true),
      onBlur: handleBlur,
      disabled: state === "disabled",
      readOnly: !allowTyping,
      onKeyDown: handleKeyInput,
      onKeyUp: handleKeyInput,
      onKeyPress: handleKeyInput,
    };

    useEffect(() => {
      if (inputValue !== undefined) {
        setValue(inputValue);
      }
    }, [inputValue]);

    const isLabelActive = isFocused || hasInputValue(value);

    const renderInputContents = () => {
      const contents = (
        <>
          {variant === "labelled" ? (
            <div
              className={`${styles.labelmarkerinput} textfield-label-marker`}
              style={{ overflow: isLabelActive ? "visible" : "hidden" }} // Necessary to prevent animation flickering
            >
              <div className={styles.labelMarker}>
                <div
                  className={`${styles.label} quill-ui-next-input-label-transition`}
                >
                  {label}
                </div>
                {(isRequired || optionalText) && (
                  <>
                    {isRequired && (
                      <div className={styles.fieldMarkerRequired}>
                        <div
                          className={`${styles.label1} quill-ui-next-input-label-transition`}
                        >
                          *
                        </div>
                      </div>
                    )}

                    {optionalText && !isRequired && (
                      <div className={styles.fieldMarkerOptional}>
                        <div
                          className={`${styles.label2} quill-ui-next-input-label-transition`}
                        >
                          {optionalText}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <AnimatePresence initial={false}>
                {inputElement === "input" ? (
                  <motion.input
                    {...inputProps}
                    ref={inputRef as RefObject<HTMLInputElement>}
                    initial={false}
                    animate={{
                      y: isLabelActive ? 0 : "100%",
                      opacity: isLabelActive ? 1 : 0,
                      scale: isLabelActive ? 1 : 0.95,
                    }}
                    transition={{
                      duration: 0.2,
                      ease: "easeOut",
                    }}
                    style={{
                      originY: 0,
                      position: "relative",
                      display: "block",
                      width: "100%",
                      pointerEvents: isLabelActive ? "auto" : "none",
                      marginTop: isLabelActive ? "" : "-25px",
                      transition: "margin-top 0.2s ease-out",
                      marginLeft: "-2px",
                    }}
                  />
                ) : (
                  <motion.textarea
                    {...inputProps}
                    ref={inputRef as RefObject<HTMLTextAreaElement>}
                    initial={false}
                    animate={{
                      y: isLabelActive ? 0 : "100%",
                      opacity: isLabelActive ? 1 : 0,
                      scale: isLabelActive ? 1 : 0.95,
                    }}
                    transition={{
                      duration: 0.2,
                      ease: "easeOut",
                    }}
                    style={{
                      originY: 0,
                      position: "relative",
                      display: "block",
                      width: "100%",
                      pointerEvents: isLabelActive ? "auto" : "none",
                      transition: "margin-top 0.2s ease-out",
                      marginLeft: "-2px",
                    }}
                  />
                )}
              </AnimatePresence>
            </div>
          ) : inputElement === "input" ? (
            <input
              ref={inputRef as RefObject<HTMLInputElement>}
              {...inputProps}
            />
          ) : (
            <textarea
              ref={inputRef as RefObject<HTMLTextAreaElement>}
              {...inputProps}
            />
          )}

          {!isGroup && <div className={styles.labelRight}>{labelRight}</div>}

          {iconExtra && (
            <div className={[styles.iconLeft, "textfield-icon"].join(" ")}>
              {iconExtra}
            </div>
          )}

          {showStatusIcon && statusIcon && !iconExtra && (
            <div
              className={[styles.iconLeft, "textfield-status-icon"].join(" ")}
            >
              {statusIcon}
            </div>
          )}

          {iconRight && (
            <div className={[styles.iconLeft, "textfield-icon"].join(" ")}>
              {iconRight}
            </div>
          )}

          {contentRight && contentRight}
        </>
      );

      if (isGroup) {
        return <div className={styles.labelinputicon}>{contents}</div>;
      }

      return contents;
    };

    return (
      <div
        className={[
          styles.root,
          className,
          "quill-ui-next-atom",
          "quill-ui-next-atom-textfield",
          "quill-ui-next-no-select",
        ].join(" ")}
        data-indicator={indicator}
        data-is-typed={isFocused}
        data-state={state}
        data-type={type}
        data-id={textFieldId}
        data-variant={variant}
        data-text-alignment={textAlignment}
        data-size={size}
        data-group={isGroup ? "true" : "false"}
        data-input-element={inputElement}
        onClick={onClick}
      >
        <div
          ref={containerRef}
          className={[
            styles.field,
            "quill-ui-next-no-focus",
            "quill-ui-next-textfield-field",
          ].join(" ")}
          onFocus={handleFocus}
          onBlur={() => state !== "disabled" && handleBlur()}
          tabIndex={0}
        >
          <div className={`${styles.field} quill-ui-next-textfield-content`}>
            {/* Normal Input */}
            {!isGroup && !isLeftGroup && (
              <>
                {iconLeft && (
                  <div
                    className={[styles.iconLeft, "textfield-icon"].join(" ")}
                  >
                    {iconLeft}
                  </div>
                )}

                <div className={styles.labelLeft}>{labelLeft}</div>
              </>
            )}

            {/*  Group Addon */}
            {isGroup && (
              <div className={styles.addonLeft}>
                <div className={styles.labelLeft}>{labelLeft}</div>
              </div>
            )}

            {/* Left Group Addon */}
            {isLeftGroup && (
              <div className="left-group-addon" onClick={onAddonLeftClick}>
                <div className={[styles.iconLeft, "textfield-icon"].join(" ")}>
                  {iconLeft}
                </div>
                <div className="left-group-addon-content">
                  <div className="left-group-addon-content-label">
                    {labelLeft}
                  </div>
                  <div className="left-group-addon-content-value">
                    {labelLeftValue}
                  </div>
                </div>
                {iconLeftSecondary && (
                  <div
                    className={[styles.iconLeft, "textfield-icon"].join(" ")}
                  >
                    {iconLeftSecondary}
                  </div>
                )}
              </div>
            )}

            {renderInputContents()}

            {isGroup && (
              <div className={styles.addonRight}>
                <div className={styles.labelRight}>{labelRight}</div>
              </div>
            )}
          </div>
          {customInnerContent && customInnerContent}
        </div>

        {/* This is required to properly set status icon color */}
        <div className={styles.message + " quill-ui-next-hidden"}></div>

        {(message || showCharacterCounter) && (
          <div className={styles.messageCharacterCounter}>
            {message && <div className={styles.message}>{message}</div>}
            {showCharacterCounter && (
              <div className={styles.characterCounter}>
                {typeof value === "string"
                  ? value.length
                  : value?.toString().length}
                /{maxLength}
              </div>
            )}
          </div>
        )}
        {messages && (
          <div className={styles.passwordStrength}>
            {messages.map(({ icon, type, text }, index) => {
              const statusType = MESSAGE_STATUS_STYLES[type];

              return (
                <div
                  key={index}
                  className={`${statusType} textfield-message-${type}`}
                >
                  {icon ? icon : MESSAGE_ICONS[type]}
                  <div className={styles.message1}>{text}</div>
                </div>
              );
            })}
          </div>
        )}
        {customContent && customContent}
      </div>
    );
  }
);

export default TextFieldAtom;
